import React, { useEffect, useRef, useState } from 'react';
import { ReactComponent as CheckIcon } from '@repo/common/assets/icons/20px/icon_20px_check-circle_outline.svg';
import { cn } from '../model/derive';
import { kstDue } from '../model/completeProject';
import TdButton from '../Button';

/**
 * 제출 성공 페이지 (2차 B14 · W10_CompleteProject_Success) — 제출 직후 한 번만 보인다.
 * 콘페티 1회(~3초, 동작 줄이기 설정이면 생략) · 정시/늦음 태그 + 실제 제출 시각 · What happens next.
 * 메뉴로 다시 들어오면 이 화면이 아니라 읽기 전용 허브가 열린다.
 */

const COLORS = ['#1dc37b', '#009e4f', '#ffdf56', '#e3b5f9', '#7eeac9', '#ff9c7a'];

/** 위에서 흩뿌려 떨어지는 종이 조각 — 외부 라이브러리 없이 캔버스로 한 번 */
const Confetti = () => {
  const ref = useRef(null);
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return undefined;
    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
    };
    resize();
    const w = () => canvas.width;
    const pieces = Array.from({ length: 140 }, () => ({
      x: w() / 2 + (Math.random() - 0.5) * w() * 0.3,
      y: canvas.height * 0.25,
      vx: (Math.random() - 0.5) * 16 * dpr,
      vy: (-Math.random() * 14 - 6) * dpr,
      size: (6 + Math.random() * 6) * dpr,
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.3,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
    }));
    const start = performance.now();
    let frame;
    const tick = (t) => {
      const elapsed = t - start;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.globalAlpha = elapsed > 2200 ? Math.max(0, 1 - (elapsed - 2200) / 800) : 1;
      pieces.forEach((p) => {
        p.vy += 0.35 * dpr;
        p.vx *= 0.99;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        ctx.restore();
      });
      if (elapsed < 3000) frame = requestAnimationFrame(tick);
      else ctx.clearRect(0, 0, canvas.width, canvas.height);
    };
    frame = requestAnimationFrame(tick);
    window.addEventListener('resize', resize);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', resize);
    };
  }, []);
  return (
    <canvas
      ref={ref}
      aria-hidden
      className='pointer-events-none fixed inset-0 z-[1100] h-full w-full'
    />
  );
};

const Next = ({ title, children }) => (
  <li className='flex flex-col gap-0.5 border-t border-green-deep03/[0.08] px-4 py-3 first:border-t-0'>
    <span className='text-[14px] font-medium leading-[140%] text-text-primary'>{title}</span>
    <span className='text-[13px] leading-[150%] text-text-secondary'>{children}</span>
  </li>
);

const FEEDBACK_MAX = 1000;

/**
 * 서비스 피드백 (선택) — 2 days project 팀만 읽는다. 한 번 보내면 카드가 닫히고 완료 문구만 남는다.
 * @param onSend (text) => Promise — 실패하면 입력을 그대로 두고 다시 보낼 수 있다
 */
const FeedbackCard = ({ onSend }) => {
  const [text, setText] = useState('');
  const [state, setState] = useState('idle'); // idle · sending · sent · failed
  const empty = !text.trim();

  if (state === 'sent') {
    return (
      <p
        role='status'
        className='mt-2 flex items-center justify-center gap-1.5 text-[14px] text-functional-success04'
      >
        <CheckIcon aria-hidden className='h-4 w-4 text-functional-success03' />
        Feedback sent. Thanks for helping us improve!
      </p>
    );
  }

  const send = async () => {
    if (empty || state === 'sending') return;
    setState('sending');
    try {
      await onSend(text.trim());
      setState('sent');
    } catch {
      setState('failed');
    }
  };

  return (
    <section className='mt-4 flex w-full flex-col gap-3 rounded-2xl border border-green-deep03/[0.06] bg-grayscale-gray00 px-4 py-5 text-left shadow-4 md:px-6'>
      <h2 className='text-[15px] font-medium leading-[140%] text-text-primary'>
        Help us make 2 days project better
      </h2>
      <p className='text-[13px] leading-[150%] text-text-secondary'>
        You’ve been through the whole project, so your view really helps. What should we keep, fix
        or add for the next team? Only the 2 days project team reads this, not your teammates.
      </p>
      <div className='flex flex-col gap-1'>
        <textarea
          rows={4}
          value={text}
          maxLength={FEEDBACK_MAX}
          disabled={state === 'sending'}
          aria-label='Your feedback'
          placeholder='For example: what felt confusing or slow, what helped most, or a feature you wished you had'
          onChange={(e) => setText(e.target.value.slice(0, FEEDBACK_MAX))}
          className='w-full resize-y rounded-xl border border-green-deep03/20 bg-grayscale-gray00 px-3 py-2.5 text-[14px] leading-[150%] text-text-primary placeholder:text-text-placeholder focus:border-green-deep02 focus:outline-none'
        />
        <span className='self-end text-[12px] tabular-nums text-text-secondary'>
          {text.length.toLocaleString('en-US')} / 1,000
        </span>
      </div>
      {state === 'failed' && (
        <p role='alert' className='text-[13px] text-functional-failed03'>
          Couldn’t send. Check your connection and try again.
        </p>
      )}
      <div className='flex md:justify-end'>
        {state === 'sending' ? (
          <button
            type='button'
            disabled
            className='h-10 w-full cursor-progress rounded-full bg-green-deep02/70 px-5 text-[14px] font-medium text-grayscale-gray00 md:w-auto'
          >
            Sending…
          </button>
        ) : (
          <TdButton disabled={empty} onClick={send} className='w-full md:w-auto'>
            Send feedback
          </TdButton>
        )}
      </div>
    </section>
  );
};

const SuccessView = ({
  submittedAt,
  late,
  onViewSubmitted,
  onBack,
  onCertificate,
  onSendFeedback,
}) => (
  <main className='flex flex-1 flex-col items-center px-4 py-12 md:py-16'>
    <Confetti />
    <div className='flex w-full max-w-[560px] flex-col items-center gap-4 text-center'>
      <span className='flex h-14 w-14 items-center justify-center rounded-2xl border border-functional-success03/40 bg-functional-success01'>
        <CheckIcon className='h-6 w-6 text-functional-success03' />
      </span>
      <h1 className='text-[26px] font-medium leading-[130%] tracking-[-0.01em] text-text-primary md:text-[28px]'>
        You’ve completed the project
      </h1>
      <p className='text-[14px] leading-[150%] text-text-secondary'>
        Thanks for staying until the end and reviewing your team honestly.
      </p>
      <span
        className={cn(
          'inline-flex h-6 items-center gap-1.5 rounded-md px-2 text-[12px] font-medium',
          late
            ? 'bg-functional-warning01 text-functional-warning04 shadow-[inset_0_0_0_1px_#ffdf56]'
            : 'bg-functional-success01 text-functional-success04 shadow-[inset_0_0_0_1px_#7eeac9]',
        )}
      >
        <span
          className={cn(
            'h-1.5 w-1.5 rounded-full',
            late ? 'bg-functional-warning03' : 'bg-functional-success03',
          )}
        />
        {late ? 'Submitted late' : 'Submitted on time'} · {kstDue(submittedAt)}
      </span>

      <section className='mt-4 w-full overflow-hidden rounded-2xl border border-green-deep03/[0.06] bg-grayscale-gray00 text-left shadow-4'>
        <h2 className='px-4 pb-1 pt-3.5 text-[14px] font-medium text-text-primary'>
          What happens next
        </h2>
        <ul>
          <Next title='Your growth report'>
            It arrives by email after every teammate’s review is in and our team checks it.
          </Next>
          <Next title='Your certificate'>
            You can view it now. It’s also attached to your report.
          </Next>
          <Next title='What you submitted'>
            Read-only from now on. You can still view every chapter.
          </Next>
          {late && (
            <Next title='Submitted late'>
              Your report shows “Peer review submitted late”. Kit reference sentences can’t be used.
            </Next>
          )}
        </ul>
      </section>

      <div className='mt-4 flex flex-wrap justify-center gap-2'>
        <TdButton
          size='l'
          onClick={onCertificate}
          disabled={!onCertificate}
          title={onCertificate ? undefined : 'Your certificate will be ready soon.'}
        >
          View certificate
        </TdButton>
        <TdButton variant='soft' size='l' onClick={onViewSubmitted}>
          View what you submitted
        </TdButton>
      </div>
      {/* 서비스 피드백 (선택) — 버튼 줄 아래, Back to workboard 위 */}
      {onSendFeedback && <FeedbackCard onSend={onSendFeedback} />}
      <TdButton
        variant='ghost'
        size='s'
        onClick={onBack}
        className='text-[14px] text-green-deep03 underline underline-offset-2 hover:text-green-deep04'
      >
        Back to workboard
      </TdButton>
    </div>
  </main>
);

export default SuccessView;
